import React, { useState, useEffect, useCallback } from 'react';
import { collection } from 'firebase/firestore';
import { db } from '../firebase';
import StatCards from './StatCards';
import { schoolClassSortOrder } from '../services/TimetableService';
import './AdminGradesView.css';
import { scopeForAdmin, adminSchoolIds, docInScope } from '../utils/schoolScope';
import { dataCacheService } from '../services/DataCacheService';

// Normalize a grade doc to a 0-100 percentage (handles string/number scores and
// docs that only carry a precomputed `percentage`).
const gradePercent = (g) => {
  const score = Number(g.score);
  const maxScore = Number(g.maxScore);
  if (Number.isFinite(score) && Number.isFinite(maxScore) && maxScore > 0) {
    return (score / maxScore) * 100;
  }
  const pct = Number(g.percentage);
  return Number.isFinite(pct) ? pct : null;
};

function AdminGradesView({ adminData }) {
  const [grades, setGrades] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('all');
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [loading, setLoading] = useState(true);
  const [classesLoaded, setClassesLoaded] = useState(false);

  // School-scoped cache key builder — shared with AdminAnalyticsView so tabs
  // reuse each other's fetched collections.
  const ck = (p) => `${adminSchoolIds(adminData).join('|') || 'all'}:${p}`;

  const loadClasses = useCallback(async () => {
    console.log('🔥 AdminGrades: Loading classes, schoolId:', adminData?.schoolId);
    try {
      let classesData = await dataCacheService.getDocs(ck('classes'), collection(db, 'classes'));
      classesData = scopeForAdmin(classesData, adminData);
      console.log('🔥 AdminGrades: Classes loaded', classesData.length, classesData);
      classesData.sort(schoolClassSortOrder);
      setClasses(classesData);
    } catch (error) {
      console.error('🔥 AdminGrades: Error loading classes:', error);
    } finally {
      setClassesLoaded(true);
    }
  }, [adminData]);

  const loadGrades = useCallback(async () => {
    console.log('🔥 AdminGrades: Loading grades');
    try {
      const schoolIds = adminSchoolIds(adminData);
      const studentsById = {};   // doc.id AND studentId code -> display name
      const studentToClass = {}; // any student identifier -> classId
      const assignmentNames = {}; // assignmentId -> title
      const assignmentToClass = {}; // assignmentId -> classId
      const classNames = {};      // classId -> class name
      classes.forEach(c => { classNames[c.id] = c.name; });

      const gradesByKey = new Map();
      const pushGrade = (docId, data, fallbackClassId, fallbackAssignmentId) => {
        const studentId = data.studentId;
        const assignmentId = data.assignmentId || fallbackAssignmentId;
        const key = `${studentId || ''}|${assignmentId || docId}`;
        if (gradesByKey.has(key)) return;
        const classId = data.classId || fallbackClassId || studentToClass[studentId] || null;
        gradesByKey.set(key, {
          ...data,
          id: docId,
          classId,
          className: classNames[classId] || data.className || 'N/A',
          assignmentId,
          assignmentName: assignmentNames[assignmentId] || data.assignmentName || 'N/A',
          studentName: studentsById[studentId] || studentId
        });
      };

      // Load students in each class for name lookup (parallel, cached)
      const perClassStudents = await Promise.all(classes.map(cls =>
        dataCacheService.getDocs(ck(`classes/${cls.id}/students`), collection(db, 'classes', cls.id, 'students'))
      ));
      classes.forEach((cls, i) => {
        perClassStudents[i].forEach(data => {
          const name = data.fullName || `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.id;
          studentsById[data.id] = name;
          studentToClass[data.id] = cls.id;
          if (data.studentId) {
            studentsById[data.studentId] = name;
            studentToClass[data.studentId] = cls.id;
          }
        });
      });

      await Promise.all(classes.map(async (cls) => {
        const assignmentDocs = await dataCacheService.getDocs(ck(`classes/${cls.id}/assignments`), collection(db, 'classes', cls.id, 'assignments'));
        const gradesPerAssignment = await Promise.all(assignmentDocs.map(a =>
          dataCacheService.getDocs(ck(`classes/${cls.id}/assignments/${a.id}/grades`), collection(db, 'classes', cls.id, 'assignments', a.id, 'grades'))
        ));
        assignmentDocs.forEach((a, i) => {
          assignmentNames[a.id] = a.title || a.id;
          assignmentToClass[a.id] = cls.id;
          gradesPerAssignment[i].forEach(g => pushGrade(g.id, g, cls.id, a.id));
        });
        // Legacy class-level grades collection: classes/{cid}/grades
        const classGradesDocs = await dataCacheService.getDocs(ck(`classes/${cls.id}/grades`), collection(db, 'classes', cls.id, 'grades'));
        classGradesDocs.forEach(g => pushGrade(g.id, g, cls.id, g.assignmentId));
      }));

      // Top-level `grades` collection (iOS batch writes — no classId field)
      try {
        const topGradesDocs = await dataCacheService.getDocs(ck('grades'), collection(db, 'grades'));
        topGradesDocs.forEach(data => {
          const inScope = docInScope(data.schoolId, schoolIds)
            || !!assignmentToClass[data.assignmentId]
            || !!studentToClass[data.studentId];
          if (inScope) {
            pushGrade(data.id, data, assignmentToClass[data.assignmentId] || studentToClass[data.studentId], data.assignmentId);
          }
        });
      } catch (e) {
        console.error('🔥 AdminGrades: top-level grades query failed (non-fatal):', e);
      }

      const gradesData = [...gradesByKey.values()];
      console.log('🔥 AdminGrades: Grades loaded', gradesData.length, gradesData);
      setGrades(gradesData);
    } catch (error) {
      console.error('🔥 AdminGrades: Error loading grades:', error);
    } finally {
      setLoading(false);
    }
  }, [classes, adminData]);

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  useEffect(() => {
    if (classesLoaded) {
      loadGrades();
    }
  }, [classesLoaded, classes, loadGrades]);

  const getFilteredGrades = () => {
    if (selectedClass === 'all') return grades;
    return grades.filter(grade => grade.classId === selectedClass);
  };

  const calculateAverage = (gradeList) => {
    const pcts = gradeList.map(gradePercent).filter(p => p !== null);
    if (pcts.length === 0) return 0;
    const sum = pcts.reduce((acc, p) => acc + p, 0);
    return (sum / pcts.length).toFixed(1);
  };

  const getGradeDistribution = (gradeList) => {
    const distribution = { A: 0, B: 0, C: 0, D: 0, F: 0 };
    gradeList.forEach(grade => {
      const percentage = gradePercent(grade);
      if (percentage === null) return;
      if (percentage >= 90) distribution.A++;
      else if (percentage >= 80) distribution.B++;
      else if (percentage >= 70) distribution.C++;
      else if (percentage >= 60) distribution.D++;
      else distribution.F++;
    });
    return distribution;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  const filteredGrades = getFilteredGrades();
  const average = calculateAverage(filteredGrades);
  const distribution = getGradeDistribution(filteredGrades);

  return (
    <div className="admin-grades-view">
      <div className="view-header">
        <h2>Grade Analytics</h2>
      </div>

      <div className="filter-bar">
        <select value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)}>
          <option value="all">All Classes</option>
          {classes.map(cls => (
            <option key={cls.id} value={cls.id}>{cls.name}</option>
          ))}
        </select>
      </div>

      <div className="stats-section">
        <h3>Overview</h3>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-info">
              <h3>{filteredGrades.length}</h3>
              <p>Total Grades</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📈</div>
            <div className="stat-info">
              <h3>{average}%</h3>
              <p>Average Score</p>
            </div>
          </div>
        </div>
      </div>

      <StatCards items={[]} charts={[
        { title: 'Class Average Grades (%)', type: 'bar', data: classes.map(c => { const pcts = grades.filter(x => x.classId === c.id).map(gradePercent).filter(p => p !== null); return { name: c.name, value: pcts.length ? Math.round(pcts.reduce((a, b) => a + b, 0) / pcts.length) : 0 }; }) },
        { title: 'Grade Distribution', type: 'bar', data: ['A', 'B', 'C', 'D', 'F'].map(k => ({ name: k, value: distribution[k] })) }
      ]} />

      <div className="stats-section">
        <h3>Grade Distribution</h3>
        <div className="grade-distribution">
          <div className="grade-bar">
            <span className="grade-label">A</span>
            <div className="bar-container">
              <div className="bar-fill" style={{ width: `${(distribution.A / filteredGrades.length) * 100 || 0}%`, background: '#22c55e' }}></div>
            </div>
            <span className="grade-count">{distribution.A}</span>
          </div>
          <div className="grade-bar">
            <span className="grade-label">B</span>
            <div className="bar-container">
              <div className="bar-fill" style={{ width: `${(distribution.B / filteredGrades.length) * 100 || 0}%`, background: '#22c55e' }}></div>
            </div>
            <span className="grade-count">{distribution.B}</span>
          </div>
          <div className="grade-bar">
            <span className="grade-label">C</span>
            <div className="bar-container">
              <div className="bar-fill" style={{ width: `${(distribution.C / filteredGrades.length) * 100 || 0}%`, background: '#f59e0b' }}></div>
            </div>
            <span className="grade-count">{distribution.C}</span>
          </div>
          <div className="grade-bar">
            <span className="grade-label">D</span>
            <div className="bar-container">
              <div className="bar-fill" style={{ width: `${(distribution.D / filteredGrades.length) * 100 || 0}%`, background: '#f97316' }}></div>
            </div>
            <span className="grade-count">{distribution.D}</span>
          </div>
          <div className="grade-bar">
            <span className="grade-label">F</span>
            <div className="bar-container">
              <div className="bar-fill" style={{ width: `${(distribution.F / filteredGrades.length) * 100 || 0}%`, background: '#ef4444' }}></div>
            </div>
            <span className="grade-count">{distribution.F}</span>
          </div>
        </div>
      </div>

      {filteredGrades.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <h3>No Grades</h3>
          <p>No grades recorded yet</p>
        </div>
      ) : (
        <div className="grades-list">
          {filteredGrades.map(grade => {
            const pct = gradePercent(grade);
            return (
              <div key={grade.id} className="grade-card" onClick={() => setSelectedGrade(grade)} style={{ cursor: 'pointer' }}>
                <div className="grade-info">
                  <h4>Assignment: {grade.assignmentName || 'N/A'}</h4>
                  <p>Student: {grade.studentName || grade.studentId || 'N/A'}</p>
                  <p>Class: {grade.className || 'N/A'}</p>
                  <p>Score: {grade.score ?? 'N/A'} / {grade.maxScore ?? 'N/A'}</p>
                  <p>Percentage: {pct === null ? 'N/A' : `${pct.toFixed(1)}%`}</p>
                </div>
                <div className="grade-display">
                  <span className="grade-percentage">{pct === null ? '—' : `${pct.toFixed(0)}%`}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedGrade && (
        <div className="modal-overlay" onClick={() => setSelectedGrade(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedGrade.assignmentName || 'Grade Detail'}</h3>
              <button className="close-button" onClick={() => setSelectedGrade(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="grade-detail-rows">
                <div className="grade-detail-row"><span>Student</span><strong>{selectedGrade.studentName || selectedGrade.studentId || 'N/A'}</strong></div>
                <div className="grade-detail-row"><span>Class</span><strong>{selectedGrade.className || 'N/A'}</strong></div>
                <div className="grade-detail-row"><span>Subject</span><strong>{selectedGrade.subjectName || selectedGrade.subject || 'N/A'}</strong></div>
                <div className="grade-detail-row"><span>Score</span><strong>{selectedGrade.score ?? 'N/A'} / {selectedGrade.maxScore ?? 'N/A'}</strong></div>
                <div className="grade-detail-row"><span>Percentage</span><strong>{gradePercent(selectedGrade) === null ? 'N/A' : `${gradePercent(selectedGrade).toFixed(1)}%`}</strong></div>
                <div className="grade-detail-row"><span>Type</span><strong>{selectedGrade.gradeType || selectedGrade.type || 'N/A'}</strong></div>
                <div className="grade-detail-row"><span>Date</span><strong>{selectedGrade.date || (selectedGrade.createdAt?.toDate ? selectedGrade.createdAt.toDate().toLocaleDateString() : 'N/A')}</strong></div>
                {selectedGrade.comments && (
                  <div className="grade-detail-row"><span>Comments</span><strong>{selectedGrade.comments}</strong></div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminGradesView;
